import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, Sparkles, Users } from 'lucide-react'
import { SidebarProvider } from '@/components/ui/sidebar'
import Navbar from '@/components/Layout/Navbar'
import OGESidebar from '@/components/OGE/Sidebar/OGESidebar'
import OGESidebarHeader from '@/components/OGE/Sidebar/OGESidebarHeader'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { getAccessToken } from '@/utils/authUtils'
import {
  connectCharterGroupWS,
  disconnectCharterGroupWS,
  loadMoreGroupMessages,
} from '../../redux/asyncThunks/charterWsThunks'
import { CharterChatMessages } from '../Shared/CharterChatMessages'
import { StageProgressBar } from '../Phase2/StageProgressBar'
import { TurnIndicator } from '../Phase2/TurnIndicator'
import { RatifiedCharterCard } from '../Charter/RatifiedCharterCard'
import { CharterCardModal } from '../Charter/CharterCardModal'
import { CharterCollaborationPanel } from '../Shared/CharterCollaborationPanel'
import {
  selectGroupMessages,
  selectGroupIsConnected,
  selectGroupIsStreaming,
  selectCurrentStage,
  selectTurnTracker,
  selectRatifiedCharter,
  selectRenegotiationMode,
  selectGroupError,
  selectCharterVersion,
  selectCharterStatus,
  selectGroupPagination,
  selectGroupIsLoadingMore,
} from '../../redux/selectors/charterSelectors'
import { toGroupDisplayMessages } from '../../utils/charterDisplayMessages'
import { buildParticipantColors } from '../../utils/participantColors'

interface GroupMonitorViewProps {
  teamId: string
  teamName: string
  roomId: string
  inviteCode: string
}

export function GroupMonitorView({
  teamId,
  teamName,
  roomId,
  inviteCode,
}: GroupMonitorViewProps) {
  const dispatch = useAppDispatch()
  const [isCharterModalOpen, setIsCharterModalOpen] = useState(false)
  const [activeAgent, setActiveAgent] = useState<'prep' | 'group'>('group')

  const messages = useAppSelector(selectGroupMessages)
  const pagination = useAppSelector(selectGroupPagination)
  const isLoadingMore = useAppSelector(selectGroupIsLoadingMore)

  const handleLoadMore = useCallback(
    () => { dispatch(loadMoreGroupMessages()) },
    [dispatch]
  )
  const isConnected = useAppSelector(selectGroupIsConnected)
  const isStreaming = useAppSelector(selectGroupIsStreaming)
  const currentStage = useAppSelector(selectCurrentStage)
  const turnTracker = useAppSelector(selectTurnTracker)
  const ratifiedCharter = useAppSelector(selectRatifiedCharter)
  const renegotiationMode = useAppSelector(selectRenegotiationMode)
  const error = useAppSelector(selectGroupError)
  const charterVersion = useAppSelector(selectCharterVersion)
  const charterStatus = useAppSelector(selectCharterStatus)

  useEffect(() => {
    const token = getAccessToken()
    if (!token || !roomId || !inviteCode || !teamId) return
    dispatch(connectCharterGroupWS({ jwtToken: token, roomId, inviteCode, teamId, monitor: true }))
    return () => {
      dispatch(disconnectCharterGroupWS())
    }
  }, [dispatch, roomId, inviteCode, teamId])

  // Shared mapper (the local copy was a near-duplicate). An empty current-user
  // id never matches a sender, so every message renders as another person's —
  // identical to the previous behavior, minus the duplication.
  const displayMessages = toGroupDisplayMessages(messages, '')

  // The professor is not a participant, so nothing is right-aligned or pink —
  // the per-sender colour is all that separates one student from another here.
  const senderColors = useMemo(() => buildParticipantColors(messages), [messages])

  const sidebarContent = (
    <div className='px-2 space-y-5'>
      {ratifiedCharter && (
        <div>
          <p className='text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1'>
            Team Charter
          </p>
          <RatifiedCharterCard
            onClick={() => setIsCharterModalOpen(true)}
            charterVersion={charterVersion ?? 1}
          />
        </div>
      )}

      <div>
        <p className='text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1'>
          Session Progress
        </p>
        <StageProgressBar
          currentStage={currentStage}
          amendmentMode={renegotiationMode}
          allComplete={!renegotiationMode && charterStatus === 'locked'}
        />
      </div>

      {Object.keys(turnTracker).length > 0 && (
        <>
          <Separator />
          <div>
            <p className='text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1'>
              Participation
            </p>
            <TurnIndicator turnTracker={turnTracker} memberNames={{}} currentUserId='' />
          </div>
        </>
      )}
    </div>
  )

  return (
    <>
      <SidebarProvider className='h-screen'>
        <OGESidebar
          header={
            <OGESidebarHeader
              userName={teamName}
              avatarSrc=''
              subText='Group Session — Phase 2'
            />
          }
          content={sidebarContent}
          chatContent={
            <CharterCollaborationPanel
              type='group'
              isConnected={isConnected}
              activeAgent={activeAgent}
              onAgentChange={setActiveAgent}
              hidePrepTab
            />
          }
          initialPanel='game'
        />

        <div className='flex flex-col w-full h-full overflow-auto'>
          {/* Phase info bar */}
          <div className='sticky top-0 z-10 px-14 py-2 ml-[2.5rem]'>
            <div className='flex items-center justify-between p-3 bg-white rounded-md shadow'>
              <div className='flex items-center gap-2'>
                {activeAgent === 'prep' ? (
                  <Sparkles className='h-5 w-5 text-indigo-500' />
                ) : (
                  <Users className='h-5 w-5 text-indigo-500' />
                )}
                <div>
                  <h3 className='text-sm font-semibold text-dark-blue flex items-center gap-2'>
                    {activeAgent === 'prep' ? 'Individual Prep' : 'Group Charter Session'}
                    {activeAgent === 'group' && renegotiationMode && (
                      <Badge className='bg-amber-100 text-amber-700 border-amber-200 text-xs'>
                        Re-negotiating
                      </Badge>
                    )}
                  </h3>
                  {activeAgent === 'group' && (
                    <p className='text-xs text-gray-500'>Phase 2 — Group Moderator</p>
                  )}
                </div>
              </div>
              <div className='flex items-center gap-2'>
                {isStreaming && (
                  <span className='flex items-center gap-1 text-xs text-indigo-500'>
                    <Loader2 className='h-3 w-3 animate-spin' />
                    Streaming…
                  </span>
                )}
                {!isConnected && (
                  <Badge variant='outline' className='text-xs text-amber-600 border-amber-300'>
                    <Loader2 className='h-3 w-3 mr-1 animate-spin' />
                    Connecting…
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Navbar — mirrors CharterGroupPage */}
          <div className='ms-6'>
            <Navbar />
          </div>

          <div className='flex overflow-x-hidden flex-col flex-1 w-full min-w-0 min-h-0'>
            <div className='flex flex-col h-full min-h-0 py-9'>
              <div className='flex flex-col flex-1 min-h-0 ml-[2.5rem] px-14'>
                {error && (
                  <p className='text-sm text-red-600 text-center mb-2'>{error}</p>
                )}
                {!isConnected && messages.length === 0 ? (
                  <div className='flex items-center justify-center flex-1 gap-2 text-gray-400'>
                    <Loader2 className='h-5 w-5 animate-spin' />
                    <span>Connecting to session…</span>
                  </div>
                ) : messages.length === 0 ? (
                  <div className='flex flex-col items-center justify-center flex-1 gap-2 text-center'>
                    <Users className='h-6 w-6 text-gray-300' />
                    <p className='text-sm text-gray-500'>
                      This team hasn’t started its group session yet.
                    </p>
                    <p className='text-xs text-gray-400'>
                      The transcript will appear here live once they begin.
                    </p>
                  </div>
                ) : (
                  <CharterChatMessages
                    messages={displayMessages}
                    agentName='Group Moderator'
                    hasMore={pagination?.has_more ?? false}
                    isLoadingMore={isLoadingMore}
                    onLoadMore={handleLoadMore}
                    senderColors={senderColors}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </SidebarProvider>

      {ratifiedCharter && (
        <CharterCardModal
          isOpen={isCharterModalOpen}
          onClose={() => setIsCharterModalOpen(false)}
          roomId={roomId}
          isStudent={false}
          teamId={Number(teamId)}
        />
      )}
    </>
  )
}
